import { useMemo } from 'react'

import type { RecentHotelSearches } from '@/components/TripActivity/types'
import { sortByCreationDateTimeDesc } from '@/components/TripActivity/utils'
import type { CityId, LOCATION_SEARCH_TYPE } from '@/types'

import useRecentSearchCard from './useRecentSearchCard'
import useRecommendations from './useRecommendations'

export type UseRecentSearchesFetchedParams = {
  cguid: string
  appName: string
  appVersion: string
  signedIn: boolean
  /** When true (DESK_MWEB_HTL_RECENT_SEARCH_LOGIC_UPDATE VARIANT), order/dedupe by newest creationDateTime. */
  sortByCreationDateTime?: boolean
}

function getRecentHotelCityIdsNewestFirst(data: ReadonlyArray<{ productType: string }>): string[] {
  const staySearches = sortByCreationDateTimeDesc(
    data.filter((item): item is RecentHotelSearches => item.productType === 'STAY'),
  )
  const cityIds: string[] = []
  const seen = new Set<string>()
  for (const item of staySearches) {
    if (!item.cityId || seen.has(item.cityId)) continue
    seen.add(item.cityId)
    cityIds.push(item.cityId)
  }
  return cityIds
}

function toLocation(item: LOCATION_SEARCH_TYPE): LOCATION_SEARCH_TYPE {
  return {
    ...item,
    country: item.countryName,
    entered: '',
    highlightedName: null,
  }
}

export default function useRecentSearchesFetched({
  cguid,
  appName,
  appVersion,
  signedIn,
  sortByCreationDateTime = false,
}: UseRecentSearchesFetchedParams) {
  const { data, isFetched: isRecentSearchCardFetched } = useRecentSearchCard(
    cguid,
    { appName, appVersion },
    ['STAY'],
    signedIn,
  )

  const cityIdList = useMemo<CityId[]>(() => {
    if (sortByCreationDateTime) {
      return getRecentHotelCityIdsNewestFirst(data).map((cityId) => ({ label: cityId }))
    }
    return data
      .filter((item) => item.productType === 'STAY')
      .map((item) => ({ label: (item as RecentHotelSearches).cityId }))
  }, [data, sortByCreationDateTime])

  const { data: cityInfoList, isFetched: isCityInfoFetched } = useRecommendations(cityIdList)

  const isReady = isRecentSearchCardFetched && (cityIdList.length === 0 || isCityInfoFetched)

  const recentLocations = useMemo<LOCATION_SEARCH_TYPE[]>(() => {
    if (!sortByCreationDateTime) {
      return cityInfoList.map(toLocation)
    }

    const cityInfoById = new Map<string, LOCATION_SEARCH_TYPE>()
    for (const cityInfo of cityInfoList) {
      const cityId = String(cityInfo.cityID ?? cityInfo.id ?? '')
      if (cityId && !cityInfoById.has(cityId)) {
        cityInfoById.set(cityId, cityInfo)
      }
    }

    return cityIdList.flatMap(({ label: cityId }) => {
      const item = cityInfoById.get(cityId)
      if (!item) return []
      return [toLocation(item)]
    })
  }, [cityIdList, cityInfoList, sortByCreationDateTime])

  return [recentLocations, isReady] as const
}
